import { useState } from "react";

import { NamePath } from "antd/lib/form/interface";
import { clsx } from "clsx";
import isEmpty from "lodash/isEmpty";

import { RVFormItem } from "../RVFormItem/RVFormItem";

import styles from "./RVFormFloatingLabel.module.scss";

/**
 * Determines whether a given value should be considered as "having a value" for floating label purposes.
 *
 * This utility function checks various value types to determine if a form field has meaningful content:
 * - Truthy values are considered as having value
 * - Zero (0) is considered as having value for numbers
 * - Empty objects/arrays are considered as not having value
 * - null/undefined are considered as not having value
 *
 * @param value - The value to check (can be any type)
 * @returns boolean - True if the value should be considered as "having a value"
 */
export const hasValue = (value: any): boolean => {
    let notEmpty = Boolean(value) || (value == 0 && typeof value === "number");
    if (typeof value === "object") {
        notEmpty = !isEmpty(value);
    }
    return notEmpty;
};

// these very clearly are used, eslint doesnt seem to like, anon
// functions and destructured props
// eslint-disable-next-line react-redux/no-unused-prop-types
/**
 * Type definition for the form field context provided by RVFormItem shouldUpdate.
 * Contains methods and properties for accessing form field values and context.
 */
// eslint-disable-next-line react-redux/no-unused-prop-types
type FormItemFieldType = { getFieldValue: any; prefixName?: NamePath };

/**
 * Props for the RVFormFloatingLabel component.
 */
type LabelProps = {
    /** The form input component to wrap with floating label behavior */
    children: JSX.Element;
    /** Form field name path (can be string or array for nested fields) */
    name?: NamePath;
    /** Whether the container should use scrollable floating label styles */
    scrollable?: boolean;
    /** Whether the floating label should remain fixed in the floating position */
    fixedFloatingLabel?: boolean;
    /** Whether the field is clearable (affects floating label behavior when focused) */
    clearable?: boolean;
};
/**
 * A floating label wrapper component that provides animated label behavior for form fields.
 *
 * This component wraps form fields with floating label functionality, where the label
 * moves to a floating position when the field has a value or is focused. It integrates
 * with Ant Design's form system and handles both simple and nested field paths.
 *
 * The component uses RVFormItem with shouldUpdate to reactively check
 * field values and determine when to float the label. It supports both regular form
 * fields and nested/prefixed field paths.
 *
 * Key features:
 * - Automatic label floating based on field value and focus state
 * - Support for nested form field paths
 * - Scrollable and fixed floating label variants
 * - Clearable field support (affects focus behavior)
 * - Integration with Ant Design form validation
 *
 * @param props - RVFormFloatingLabel component props
 * @param props.children - RVFormItem component with input field
 * @param props.name - Form field name path (string or array for nested fields)
 * @param props.scrollable - Whether to use scrollable floating label styles
 * @param props.fixedFloatingLabel - Whether the label should stay fixed in floating position
 * @param props.clearable - Whether the field is clearable (affects floating behavior)
 * @returns JSX.Element - The floating label wrapper component
 */
export function RVFormFloatingLabel({
    children,
    name,
    scrollable,
    fixedFloatingLabel,
    clearable = false,
}: LabelProps): JSX.Element {
    const [focused, setFocused] = useState(false);
    const floatClass = scrollable ? "floating-label-scrollable" : "floating-label";

    return (
        <RVFormItem noStyle shouldUpdate>
            {({ getFieldValue, prefixName }: FormItemFieldType) => {
                // this was the original logic in this component, but it wasnt always correct
                const prefixPath = Array.isArray(prefixName) ? prefixName : [prefixName];
                const namePath = Array.isArray(name) ? name : name ? [name] : [];
                const path = prefixName ? [...prefixPath, ...namePath] : namePath;
                const pathValue = getFieldValue(path);

                // this logic is from a copy pasted version of this component.
                // and this works. so we are falling back to this logic
                // further investigation needs to be done to figure out why
                // the original logic wasnt working
                const nameValue = getFieldValue(name);
                const shouldFloatLabel = hasValue(pathValue) || hasValue(nameValue) || (focused && !clearable);
                return (
                    <div
                        onBlur={() => setFocused(false)}
                        onFocus={() => setFocused(true)}
                        className={
                            shouldFloatLabel
                                ? clsx(styles.floatLabel, floatClass, {
                                      ["fixed-floating-label"]: fixedFloatingLabel,
                                  })
                                : ""
                        }
                    >
                        {children}
                    </div>
                );
            }}
        </RVFormItem>
    );
}
